import { productPath } from '../services/productPath';
import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ShoppingCart, Loader2, CheckCircle2, Truck, Boxes, Repeat, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import BrandLogo from './BrandLogo';
import TopProductBadge from './TopProductBadge';
import WishlistButton from './WishlistButton';
import CompareButton from './CompareButton';
import { availabilityOf } from '../services/stock';
import { SearchHighlight, matchesTokens } from './SearchHighlight';
import BestPriceBadge from './BestPriceBadge';
import FuelBadge from './FuelBadge';
import { useT } from '../services/i18n';
import { prodName, useTerms } from '../services/catalogI18n';

const matchedAltRef = (p, tokens) => {
  if (!tokens?.length || matchesTokens(p.ref, tokens) || matchesTokens(p.name, tokens) || matchesTokens(p.brand, tokens)) return null;
  const digits = tokens.join('').replace(/\D/g, '');
  if (digits.length >= 8 && (p.ean || '').replace(/\D/g, '') === digits) return { ref: p.ean, ean: true };
  const rb = p.ref_brands || {};
  const hit = (r) => matchesTokens(r, tokens) || (rb[r] && matchesTokens(`${rb[r]} ${r}`, tokens));
  const alt = (p.refs || []).find(hit);
  if (alt) return { ref: rb[alt] ? `${rb[alt]} ${alt}` : alt, oem: false };
  const oem = (p.oem_refs || []).find(hit);
  return oem ? { ref: rb[oem] ? `${rb[oem]} ${oem}` : oem, oem: true } : null;
};

const ProductCard = ({ product, onAddToCart, isAdding, compatible, highlightTokens = [], equivalent = false, linkState = null, isNew = false }) => {
  const { t, lang } = useT();
  const tt = useTerms(lang);
  const name = prodName(product, lang);
  const avail = availabilityOf(product);
  const alt = matchedAltRef(product, highlightTokens);
  return (
  <div data-testid={`product-card-${product.id}`} className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow group flex flex-col">
    <div className="flex items-center justify-between mb-2 min-h-[24px]">
      {product.discount ? (
        <span className="inline-block bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
          -{product.discount}%
        </span>
      ) : <span />}
      <span className="flex items-center gap-1">
        {isNew && (
          <span data-testid={`new-badge-${product.id}`} className="inline-flex items-center gap-1 bg-sky-50 text-sky-700 text-xs font-semibold px-2 py-1 rounded-full border border-sky-200 animate-in fade-in zoom-in-95">
            <Sparkles size={12} /> {t('common.new')}
          </span>
        )}
        {compatible && (
          <span data-testid="compatible-badge" className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs font-medium px-2 py-1 rounded-full border border-green-200">
            <CheckCircle2 size={12} /> {t('common.compatible')}
          </span>
        )}
        <WishlistButton productId={product.id} size={16} />
      </span>
    </div>

    <Link to={productPath(product)} state={linkState} data-testid={`product-link-${product.id}`} className="h-32 flex items-center justify-center mb-4">
      <img src={product.image} alt={name} className="max-h-full object-contain group-hover:scale-105 transition-transform" />
    </Link>

    <div className="flex items-center gap-1 mb-2">
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={14} className={i < Math.floor(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
      ))}
      <span className="text-xs text-gray-500">({product.reviews})</span>
      <TopProductBadge product={product} className="ml-auto" />
    </div>

    <p className="text-xs text-gray-500 font-medium"><BrandLogo brand={product.brand} size="xs" testId={`product-card-brand-${product.id}`} /></p>
    <h3 className="font-medium text-gray-900 text-sm mb-1 group-hover:text-orange-600 transition-colors">
      <Link to={productPath(product)} state={linkState} data-testid={`product-title-link-${product.id}`}><SearchHighlight text={name} tokens={highlightTokens} /></Link>
    </h3>
    <p className="text-xs text-gray-400 mb-1">{t('common.ref')} <SearchHighlight text={product.ref} tokens={highlightTokens} />{matchesTokens(product.brand, highlightTokens) && <> · <SearchHighlight text={product.brand} tokens={highlightTokens} /></>}</p>
    {product.fuel_types?.length > 0 && <div className="mb-1"><FuelBadge types={product.fuel_types} testId={`product-card-fuel-${product.id}`} /></div>}
    {alt && <p className="text-[11px] text-blue-700 mb-1" data-testid={`product-card-matched-ref-${product.id}`}>{alt.ean ? t('header.matchesEan') : t('header.matchesRef', { kind: alt.oem ? t('common.oem') : t('common.alternative') })}<SearchHighlight text={alt.ref} tokens={highlightTokens} /></p>}
    {equivalent && <p className="text-[11px] text-blue-700 mb-1 inline-flex items-center gap-1" data-testid={`product-card-equivalent-${product.id}`}><Repeat size={11} /> {t('common.equivalentOther')}</p>}
    {product.type && <p className="text-xs text-gray-500 mb-2 line-clamp-1">{tt(product.type)}</p>}

    <div className="flex items-baseline gap-2 mb-1 mt-auto">
      {product.original_price && (
        <span className="text-sm text-gray-400 line-through">{product.original_price.toFixed(2)} €</span>
      )}
      <span className="text-xl font-bold text-gray-900">{product.price.toFixed(2)} €</span>
    </div>
    {product.best_price?.active && <div className="mb-1"><BestPriceBadge product={product} /></div>}
    <p className={`text-[11px] mb-3 flex items-center gap-1 ${avail.status === 'out' ? 'text-red-600' : avail.status === 'supplier' ? 'text-blue-700' : avail.status === 'low' ? 'text-orange-600' : 'text-green-700'}`} data-testid={`availability-${product.id}`}>
      {avail.status === 'supplier' ? <><Truck size={11} /> {t('common.supplierStock', { n: avail.lead })}</> : avail.status === 'out' ? t('common.outOfStock') : avail.status === 'low' ? t('common.lowStock', { n: avail.warehouse }) : t('common.inStock')}
      {product.packs?.length > 0 && <span className="ml-auto text-gray-500 flex items-center gap-1" data-testid={`pack-badge-${product.id}`}><Boxes size={11} /> {t('common.packAvailable', { label: tt(product.packs[0].label).toLowerCase() })}</span>}
    </p>
    <div className="mb-3"><CompareButton product={product} /></div>

    {avail.status === 'out' ? (
      <span data-testid={`out-of-stock-${product.id}`} className="w-full inline-flex items-center justify-center h-9 rounded-md bg-gray-100 text-gray-500 text-sm font-medium">{t('common.outOfStock')}</span>
    ) : (
    <Button
      data-testid={`add-to-cart-${product.id}`}
      className="w-full bg-orange-500 hover:bg-orange-600 text-sm"
      onClick={() => onAddToCart(product.id)}
      disabled={isAdding}
    >
      {isAdding ? <Loader2 size={16} className="animate-spin mr-2" /> : <ShoppingCart size={16} className="mr-2" />}
      {t('common.addToCart')}
    </Button>
    )}
  </div>
  );
};

export default ProductCard;
